/** @module csi/canvas/ImageReviewWidget */
define([
	"dojo/_base/declare",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"csi",
	"csi/_base/underscore",
	"csi/comm/PostRequest",
	"csi/html",
	"csi/html/Widget",
	"csi/image/_base",
	"csi/locale",
	"csi/core/Key",
	"csi/reflection",
	"csi/store/Nodes",
	"csi/types",
	"csi/widget/_svg/util",
	"csi/widget/SelectList",
	"csi/xml",
	"dojo/text!csi/canvas/ImageReviewWidget.html",
	"dojo/text!csi/canvas/ImageReviewWidgetField.html",
	"dojo/text!csi/canvas/ImageReviewWidgetImage.html"
], function(declare, _TemplatedMixin, _WidgetBase, csi, _, PostRequest, html, Widget, csiImage, locale, Key,
	reflection, Nodes, types, svgUtil, SelectList, xml, template, fieldTemplate, imageTemplate) {

	const URL_NO_IMAGE = "/WebAccess/Images/NoImage.jpg";

	/**
	 * Widget to render new/old pair of placeholder image attributes
	 */
	var _ImageWidget = declare("ImageWidget", [_WidgetBase, _TemplatedMixin], {
		templateString: imageTemplate,
		buildRendering: function() {
			this.inherited(arguments);
			csiImage.create(null, this.imageOldSource || URL_NO_IMAGE, this.oldImageNode);
			csiImage.create(null, this.imageNewSource || URL_NO_IMAGE, this.newImageNode);
		}
	});

	/**
	 * Widget to render new/old pair of placeholder string attributes
	 */
	var _FieldWidget = declare("FieldWidget", [_WidgetBase, _TemplatedMixin], {
		templateString: fieldTemplate,

		latestValueRichTextNode: null,
		originalValueRichTextNode: null,
	});

	/**
	 * Main widget
	 */
	return declare(Widget, {
		templateString: template,

		/** @property {number} currentItem - the url of the selected item */
		currentItem: 0,

		/** @property {Object[]} data - placeholder fields and images data */
		_data: null,

		/** @property {csi/widget/SelectList} selectList - control with list of images to review */
		selectList: null,

		/** @property {Memory} store - image nodes store */
		store: null,

		postCreate: function(dfds) {
			this.inherited(arguments);

			var self = this;
			this.selectList = new SelectList({
				'class': 'csi-imgrv-select',
				maxWidth: 250,
				selectedFirst: false,
				singleSelect: true,
				showFilter: false,
				value: [],
				onItemChange: function(itemUrl) {
					self._setIndex(itemUrl[0]);
				}
			}).placeAt(this.selectNode);

			this.getPlaceholderChanges();

		},

		/**
		 * Parse placeholder changes and put them on data property
		 */
		getPlaceholderChanges: function() {
			// ph == stands for PlaceHolder
			var view = this.getSourceController().widget;
			var data = view.canvasWidgetNode.placeholderDifferenceData;

			// If we have no placeholders - return now
			if (!data) {
				return;
			}
			// Split by placeholders
			var phPlaceholders = {};
			data = svgUtil.flattenPlaceholderData(data);

			// Make data unique as multiple placeholders could access same attributes populated from same node.
			data = _.uniq(data, (item) => {
				// Combination of attributeKey and nodeURL is unique.
				return item.attributeKey + item.nodeURL;
			});

			data.forEach((field) => {
				const url = field.url || csi.EMPTY_URL; // if node is undefined, use centric:
				const cache = view.getCache();
				const node = cache.getNode(url);
				if (!phPlaceholders[url]) {
					phPlaceholders[url] = {};
					phPlaceholders[url].fields = [];
				}

				// Use type for everything except auto fill placeholders.
				if (!node) {
					phPlaceholders[url].$Name = locale.type(field.type);
				} else if (view.getViewNodeType() === node.$Type) {
					phPlaceholders[url].$Name = locale.resource("ContextAttributes");
				} else {
					phPlaceholders[url].$Name = locale.getNodeNameOrType(node, cache);
				}

				phPlaceholders[url].$URL = url;
				phPlaceholders[url].fields.push(field);

			});
			data = _.values(phPlaceholders);
			this._data = data;
		},

		/**
		 * @override
		 * Send request for images with successors in current canvas
		 */
		_getRootRequest: function(node) {
			if (node.$Type == "CanvasTemplate") {
				return this.inherited(arguments);
			} else if (svgUtil.isNonRevisionCanvas(node)) {
				return new PostRequest("SiteAdmin", "CanvasGetImagesWithSuccessor").addParameter("URL", node.$URL);
			} else {
				return new PostRequest("SiteAdmin", "CanvasGetImagesWithSuccessor").addParameter("URL", node.__Parent__);
			}
		},

		/**
		 * @override
		 * Parse request results, combine placeholder data with this results, sort, create store
		 */
		onViewRootResponse: function(res) {
			let nodes = res.getResultNodes();
			// Parse images data (if any)
			if (nodes.length) {
				nodes.forEach((node) => {
					node.newNode = this.cacheView.getNode(node.Successor);
				});
				// Merge with placeholder data
				if (this._data) {
					nodes = nodes.concat(this._data);
				}
				this._data = xml.sortNodes(nodes);
			}

			const data = this._data;
			// create store
			const store = this.store = new Nodes({
				nameProperty: "$Name",
				data,
			});
			// Set store and list selection
			itemUrl = data[0].$URL;
			this.selectList.set("store", store);
			this.selectList.set("value", [itemUrl]);
			this._setIndex(itemUrl);
		},

		/**
		 * Detect if this is image change or placeholder change and initiate preview pane creation
		 * @param {string} url
		 */
		_createPreview: function(url) {
			const node = this.store.get(url);
			if (node.fields) {
				this._createPhReview(node);
			} else {
				this._createImageReview(node);
			}
		},

		/**
		 * Create placeholder changes preview pane
		 * @param {Node} node
		 */
		_createPhReview: function(node) {
			html.clear(this.fieldsNode, true);
			node.fields.forEach((field) => {
				const fieldsCss = "csi-rv-field" + (field.changed ? " csi-rv-changed" : "");
				let restoreValue;
				const displayName = svgUtil.getDisplayNameForKey(field.attributeKey);
				if (field.restoreToDefaultView) {
					// Use the key to get restore value.
					restoreValue = "{Value}";
					field.name = displayName;
				}

				if (field.isImage) {
					// original and latest value might be "Default" for default images, or could be an image label,
					// or could be an empty string, so to ensure proper "no image" icon displays for both original
					// and latest we ensure empty strings will be fed to image/_base instance.
					const oldSource = svgUtil.hasURLQueryParameter(field.originalValue) ? field.originalValue : "";
					const newSource = svgUtil.hasURLQueryParameter(field.latestValue) ? field.latestValue : "";

					new _ImageWidget({
						"class": fieldsCss,
						fieldName: field.name,
						imageOldSource: oldSource,
						imageNewSource: newSource
					}).placeAt(this.fieldsNode);
				} else if (field.format === types.FORMAT_RICH) {
					const label = `${displayName}:`;
					const widget = new _FieldWidget({
						"class": fieldsCss,
						originalValue: label,
						latestValue: label,
					})
					widget.originalValueRichTextNode.innerHTML = field.originalValue;
					widget.latestValueRichTextNode.innerHTML = field.latestValue;
					widget.placeAt(this.fieldsNode);
				} else {
					new _FieldWidget({
						"class": fieldsCss,
						originalValue: `${displayName}: ${field.originalValue}`,
						latestValue: `${displayName}: ${restoreValue || field.latestValue}`,
					}).placeAt(this.fieldsNode);
				}
			});
		},

		/**
		 * Create pair of images: Predecessor (old one) and successor (new ome) images
		 * @param {imageNode} imageNode
		 */
		_createImageReview: function(imageNode) {
			html.clear(this.fieldsNode, true);
			var srcOld = csiImage.getImageSrc(imageNode, null, reflection.ATTR_MEDIUM_IMAGE);
			var srcNew = csiImage.getImageSrc(imageNode.newNode, null, reflection.ATTR_MEDIUM_IMAGE);
			new _ImageWidget({
				"class": "csi-rv-changed",
				fieldName: locale.resource("Image"),
				imageOldSource: srcOld,
				imageNewSource: srcNew
			}).placeAt(this.fieldsNode);
		},

		/**
		 * Set selection index
		 * @param {string} url
		 */
		_setIndex: function(url) {
			this.currentItem = url;
			this._createPreview(url);

			const {store} = this;
			const index = store.resultUrls.indexOf(url);
			const pages = store.getSize();
			if (pages <= 1) {
				this.prevPageButton.set("disabled", true);
				this.nextPageButton.set("disabled", true);
			}
			html.setInnerText(this.imageNumNode, (index + 1) + " / " + pages);
		},

		/**
		 * "Previous image" button click
		 */
		_onPrevClick: function() {
			const {store} = this;
			const pages = store.getSize();
			if (pages <= 1) {
				return;
			}

			let index = store.resultUrls.indexOf(this.currentItem);
			// Select previous or jump over to the last item
			index = (index <= 0) ? pages - 1 : index - 1;
			const newPageUrl = store.resultUrls[index];
			this.selectList.set("value", [newPageUrl]);
			this._setIndex(newPageUrl);
		},


		/**
		 * "Next Image" button click
		 */
		_onNextClick: function() {
			const {store} = this;
			const pages = store.getSize();
			if (pages <= 1) {
				return;
			}

			let index = store.resultUrls.indexOf(this.currentItem);
			// Select next or jump over to the first item
			index = (index >= (pages - 1)) ? 0 : index + 1;
			const newPageUrl = store.resultUrls[index];
			this.selectList.set("value", [newPageUrl]);
			this._setIndex(newPageUrl);
		}

	});
});
